import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { createFilterOptions } from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useForm } from "react-hook-form";
import ModalBasicLayout from "../../../../components/Modal/ModalBasicLayout";
import ContainerModalActions from "../../../../components/Containers/ContainerModalActions";
import TextField from "../../../../components/Form/TextField";
import AutocompleteField from "../../../../components/Form/AutocompleteField";
import Loader from "../../../../components/Loaders/Loader";
import { SimpleAlert } from "../../../../components/Alerts/Alerts";
import { useTranslationApp } from "../../../../lib/i18next";
import { useDispatchApp } from "../../../../lib/redux";
import {
  startGetCampaignKpiTargets,
  startUpdateCampaignKpiTargets,
} from "../../../../actions/adsCampaigns";
import { startGetAdvertiserKpiSummary } from "../../../../actions/user";
import {
  ADMIN_KPI_CPA_CURRENCY_CODES,
  ADMIN_PRIMARY_KPI_OPTIONS,
} from "../../../../utils/options";
import {
  BUTTON_CANCEL,
  BUTTON_SAVE,
  FIELD_REQUIRED,
  MODAL_CAMPAIGN_KPI_CPA_INHERIT_HINT,
  MODAL_CAMPAIGN_KPI_CPA_TITLE,
  MODAL_CAMPAIGN_KPI_CPA_WEEKLY_VOLUME_LABEL,
  MODAL_MODIFY_KPI_CPA_CURRENCY_LABEL,
  MODAL_MODIFY_KPI_CPA_PRIMARY_LABEL,
  MODAL_MODIFY_KPI_CPA_SAVE_SUCCESS,
  MODAL_MODIFY_KPI_CPA_TARGET_LABEL,
} from "../../../../i18n/keysTranslations";

const DEFAULT_CURRENCY = "USD";

const normalizeCurrency = (value) =>
  typeof value === "string" && value.trim()
    ? value.trim().toUpperCase()
    : DEFAULT_CURRENCY;

const getAccountTargetByPlatform = ({ accountKpiSummary, platform }) => {
  const parts = Array.isArray(accountKpiSummary?.targetCpaParts)
    ? accountKpiSummary.targetCpaParts
    : [];
  return parts.find((part) => part?.platform === platform) || null;
};

const parseTargetCpa = (raw) => parseFloat(String(raw ?? "").replace(",", "."));

const hasValidTargetCpa = (raw) => {
  const n = parseTargetCpa(raw);
  return Number.isFinite(n) && n > 0;
};

const parseVolume = (raw) => {
  const n = parseFloat(String(raw ?? "").replace(",", "."));
  return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
};

/**
 * Each platform carries its own goal:
 * { primaryKpi, targetCpa, currency, volume }.
 * volume is the weekly result target for that KPI.
 * Saved as platformsData.{platform}.kpiSettings.
 */
const buildDefaultValues = ({ platforms, kpiTargets, accountKpiSummary }) => {
  const platformTargets = {};
  platforms.forEach((platform) => {
    const row = kpiTargets?.platformTargets?.[platform];
    const accountRow = getAccountTargetByPlatform({
      accountKpiSummary,
      platform,
    });
    const volume = parseVolume(row?.volume);
    platformTargets[platform] = {
      primaryKpi:
        row?.primaryKpi?.trim() || kpiTargets?.primaryKpi?.trim() || null,
      targetCpa: hasValidTargetCpa(row?.targetCpa) ? String(row.targetCpa) : "",
      volume: volume ? String(volume) : "",
      currency: normalizeCurrency(row?.currency ?? accountRow?.currency),
    };
  });

  return { platformTargets };
};

/**
 * Returns null when no platform has a complete goal (KPI, volume and CPA),
 * which clears the campaign goals so the account ones apply again.
 */
const buildPayloadFromValues = ({ values, platforms }) => {
  const platformTargets = {};
  platforms.forEach((platform) => {
    const row = values.platformTargets?.[platform];
    const primaryKpi = (row?.primaryKpi ?? "").toString().trim();
    const volume = parseVolume(row?.volume);
    if (!primaryKpi || !volume || !hasValidTargetCpa(row?.targetCpa)) {
      return;
    }
    platformTargets[platform] = {
      primaryKpi,
      targetCpa: parseTargetCpa(row.targetCpa),
      currency: normalizeCurrency(row?.currency),
      volume,
    };
  });

  return Object.keys(platformTargets).length === 0 ? null : { platformTargets };
};

const ModalCampaignKpiCpa = ({
  modalOpen,
  onCloseModal,
  campaignID,
  platforms = [],
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { setValue } = useFormContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: buildDefaultValues({
      platforms,
      kpiTargets: null,
      accountKpiSummary: null,
    }),
  });

  // Before the first paint, so the empty defaults never flash ahead of the loader.
  useLayoutEffect(() => {
    if (modalOpen) {
      setIsLoading(true);
    }
  }, [modalOpen]);

  useEffect(() => {
    if (!modalOpen || !campaignID) {
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const targetsResponse = await dispatch(
          startGetCampaignKpiTargets({ campaignID })
        );
        const accountResponse = targetsResponse?.advertiserID
          ? await dispatch(
              startGetAdvertiserKpiSummary({
                advertiserID: targetsResponse.advertiserID,
              })
            )
          : null;

        if (cancelled) {
          return;
        }
        const nextKpiTargets = targetsResponse?.kpiTargets ?? null;
        const nextAccountKpiSummary = accountResponse?.kpiSummary ?? null;
        reset(
          buildDefaultValues({
            platforms,
            kpiTargets: nextKpiTargets,
            accountKpiSummary: nextAccountKpiSummary,
          })
        );
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [modalOpen, campaignID]);

  const primaryKpiOptions = useMemo(
    () =>
      ADMIN_PRIMARY_KPI_OPTIONS.map(({ value }) => ({ value, label: value })),
    []
  );

  const currencyOptions = useMemo(
    () =>
      ADMIN_KPI_CPA_CURRENCY_CODES.map((code) => ({
        value: code,
        label: code,
      })),
    []
  );

  const kpiFilterOptions = useMemo(
    () =>
      createFilterOptions({
        matchFrom: "any",
        stringify: (option) =>
          `${option?.value ?? ""} ${t(option?.label ?? "")}`,
      }),
    [t]
  );

  const currencyFilterOptions = useMemo(
    () =>
      createFilterOptions({
        matchFrom: "any",
        stringify: (option) => `${option?.value ?? ""} ${option?.label ?? ""}`,
      }),
    []
  );

  // A platform goal needs KPI, weekly volume and CPA together; all empty means "use account".
  const getPlatformRules = useMemo(
    () => (platform) => {
      const isRowTouched = (formValues) => {
        const row = formValues?.platformTargets?.[platform];
        return {
          hasKpi: Boolean(String(row?.primaryKpi ?? "").trim()),
          hasCpa: hasValidTargetCpa(row?.targetCpa),
          hasVolume: Boolean(parseVolume(row?.volume)),
        };
      };
      return {
        kpi: {
          validate: (value, formValues) => {
            const touched = isRowTouched(formValues);
            const missingKpi = !String(value ?? "").trim();
            return (touched.hasCpa || touched.hasVolume) && missingKpi
              ? t(FIELD_REQUIRED)
              : true;
          },
        },
        cpa: {
          validate: (_value, formValues) =>
            isRowTouched(formValues).hasKpi && !isRowTouched(formValues).hasCpa
              ? t(FIELD_REQUIRED)
              : true,
        },
        volume: {
          validate: (_value, formValues) =>
            isRowTouched(formValues).hasKpi &&
            !isRowTouched(formValues).hasVolume
              ? t(FIELD_REQUIRED)
              : true,
        },
      };
    },
    [t]
  );

  const onSubmit = async (values) => {
    const nextKpiTargets = buildPayloadFromValues({ values, platforms });
    setIsSubmitting(true);
    try {
      const response = await dispatch(
        startUpdateCampaignKpiTargets({
          campaignID,
          kpiTargets: nextKpiTargets,
          platforms,
        })
      );
      if (response?.ok) {
        platforms.forEach((platform) => {
          const row = nextKpiTargets?.platformTargets?.[platform];
          setValue(
            `platformsData.${platform}.kpiSettings`,
            row
              ? {
                  primaryKpi: row.primaryKpi,
                  targetCpa: row.targetCpa,
                  targetCpaCurrency: row.currency,
                  ...(row.volume ? { volume: row.volume } : {}),
                }
              : null,
            { shouldDirty: true, shouldTouch: true, shouldValidate: true }
          );
        });
        await SimpleAlert({
          title: t(MODAL_MODIFY_KPI_CPA_SAVE_SUCCESS),
          icon: "success",
        });
        onCloseModal();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const emptyRules = { required: false };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      title={t(MODAL_CAMPAIGN_KPI_CPA_TITLE)}
      maxWidth="sm"
      fullWidth
      showCloseButton={true}
    >
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <Loader size={32} hasMarginTop={false} />
        </Box>
      ) : (
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
          }}
        >
          {platforms.map((platform) => {
            const rules = getPlatformRules(platform);
            return (
              <Box
                key={platform}
                sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
              >
                <Typography variant="subtitle2" fontWeight={600}>
                  {t(platform)}
                </Typography>
                <AutocompleteField
                  fullWidth
                  label={t(MODAL_MODIFY_KPI_CPA_PRIMARY_LABEL)}
                  name={`platformTargets.${platform}.primaryKpi`}
                  variant="filled"
                  color="primary"
                  control={control}
                  rules={rules.kpi}
                  errors={errors}
                  options={primaryKpiOptions}
                  doTranslate
                  filterOptions={kpiFilterOptions}
                />
                <TextField
                  fullWidth
                  label={t(MODAL_CAMPAIGN_KPI_CPA_WEEKLY_VOLUME_LABEL)}
                  name={`platformTargets.${platform}.volume`}
                  variant="filled"
                  color="primary"
                  InputLabelProps={{ shrink: true }}
                  control={control}
                  rules={rules.volume}
                  errors={errors}
                  type="number"
                  inputProps={{ min: 1, step: 1 }}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 1.5,
                    alignItems: { sm: "flex-start" },
                  }}
                >
                  <TextField
                    fullWidth
                    label={t(MODAL_MODIFY_KPI_CPA_TARGET_LABEL)}
                    name={`platformTargets.${platform}.targetCpa`}
                    variant="filled"
                    color="primary"
                    InputLabelProps={{ shrink: true }}
                    control={control}
                    rules={rules.cpa}
                    errors={errors}
                    type="number"
                  />
                  <Box sx={{ width: { xs: "100%", sm: 180 }, flexShrink: 0 }}>
                    <AutocompleteField
                      fullWidth
                      label={t(MODAL_MODIFY_KPI_CPA_CURRENCY_LABEL)}
                      name={`platformTargets.${platform}.currency`}
                      variant="filled"
                      color="primary"
                      control={control}
                      rules={emptyRules}
                      errors={errors}
                      options={currencyOptions}
                      filterOptions={currencyFilterOptions}
                    />
                  </Box>
                </Box>
              </Box>
            );
          })}

          <Typography variant="caption" color="text.secondary">
            {t(MODAL_CAMPAIGN_KPI_CPA_INHERIT_HINT)}
          </Typography>

          <ContainerModalActions
            hasPadding={false}
            propsSecondaryButton={{
              showButton: true,
              text: BUTTON_CANCEL,
              onClick: onCloseModal,
            }}
            propsPrimaryButton={{
              showButton: true,
              text: BUTTON_SAVE,
              loading: isSubmitting,
              type: "submit",
              onClick: () => {},
            }}
          />
        </Box>
      )}
    </ModalBasicLayout>
  );
};

export default ModalCampaignKpiCpa;
